import { QuestionType, type Question } from '@/types/question';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import QuestionNav from '../components/SimulationFlow/QuestionNav';
import QuestionHeader from '../components/SimulationFlow/QuestionHeader';
import QuestionBody from '../components/SimulationFlow/QuestionBody';
import PageWhite from '../components/General/PageWhite';
import Span from '../components/Typography/Span';
import { Icon } from '@testbusters/icon';
import { LaTeXRenderer } from '../components/LaTeX/LaTeXRenderer';
import FullscreenImage from '../components/General/FullscreenImage';
import {
  useGetFavouriteQuestions,
  useGetFavouriteQuestion,
  useGetWrongQuestions,
  useGetWrongQuestion,
  useRemoveFavouriteQuestion,
} from '../hooks/favouriteQuestions/useFavouriteQuestions';

import Loader from '../components/Loader/Loader';
import GeneralError from '../components/Errors/General';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/mie-domande/$id')({
  validateSearch: (search: Record<string, unknown>): { type?: 'favourites' | 'wrong' } => ({
    type: search.type === 'wrong' ? 'wrong' : search.type === 'favourites' ? 'favourites' : undefined,
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const { type } = Route.useSearch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isWrong = type === 'wrong';

  const { favourites } = useGetFavouriteQuestions();
  const { questions: wrongQuestions } = useGetWrongQuestions();
  const { question: favQuestion, isFetching: favFetching, error: favError } = useGetFavouriteQuestion(isWrong ? '' : id);
  const { question: wrongQuestion, isFetching: wrongFetching, error: wrongError } = useGetWrongQuestion(isWrong ? id : '');

  const navList = isWrong ? wrongQuestions : favourites;
  const question = isWrong ? wrongQuestion : favQuestion;
  const isFetching = isWrong ? wrongFetching : favFetching;
  const error = isWrong ? wrongError : favError;

  const questionIds = navList.map((q) => q.questionId);
  const initialIndex = Math.max(questionIds.indexOf(id), 0);
  const [questionIndex, setQuestionIndex] = useState(initialIndex);

  const currentId = questionIds[questionIndex];

  useEffect(() => {
    if (currentId && currentId !== id) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      navigate({ to: '/mie-domande/$id', params: { id: currentId }, search: { type } as any });
    }
  }, [questionIndex]);

  const removeMutation = useRemoveFavouriteQuestion();

  const handleRemove = () => {
    removeMutation.mutate(id, {
      onSuccess: () => {
        toast.success(t('favouriteQuestions.removeSuccess'));
        navigate({ to: '/mie-domande' });
      },
      onError: () => toast.error(t('common.error')),
    });
  };

  // Map FavouriteQuestionDetail → Question for existing components
  const questionForComponents: Question | null = question
    ? {
        ...question,
        subject: { _id: question.subjectId, name: question.subject.name },
        topic: { _id: question.topicId, name: question.topic.name },
        completionType: question.isCompletion ?? question.type === 'completion',
        language: question.language as Question['language'],
        type: question.type as QuestionType | undefined,
        approved: question.approved ?? false,
        createdAt: question.createdAt ?? '',
        updatedAt: question.updatedAt ?? '',
      }
    : null;

  if (isFetching) return <Loader fullScreen />;
  if (error || !questionForComponents) return <GeneralError />;

  // Build minimal question list for QuestionNav
  const questionsForNav = navList.map((q) => ({
    ...questionForComponents,
    _id: q.questionId,
    questionText: q.questionText,
  })) as Question[];

  return (
    <PageWhite className="min-h-screen flex flex-col justify-between gap-8 p-0!">
      <div className="flex flex-col px-4 pt-5 pb-5 lg:px-11 lg:pt-8 lg:pb-7 gap-7">
        <div className="flex w-full justify-start items-center">
          <Link
            to="/mie-domande"
            className="w-fit flex items-center gap-2"
          >
            <Icon
              name="arrow-left"
              className="text-xl"
            />
            <Span className="font-semibold">{t('common.close')}</Span>
          </Link>
        </div>
        <div className="flex flex-col lg:flex-row gap-0">
          {/* Left: question + alternatives */}
          <div className="flex-1 flex flex-col gap-4 lg:pr-6">
            <div className="flex items-center gap-3">
              <img
                src="/icons/thunder.svg"
                width={16}
                height={16}
              />
              <Span className="font-semibold">
                {t('favouriteQuestions.title')}
              </Span>
            </div>
            <QuestionHeader
              question={questionForComponents}
              questionIndex={questionIndex}
              isLiked={true}
              // isSaved={false}
              readonly
              onRemove={isWrong ? undefined : handleRemove}
            />
            <QuestionBody
              question={questionForComponents}
              selectedAnswer={
                questionForComponents.alternatives?.find((a) => a.correct)?.text
              }
              readonly
            />
          </div>

          {/* Right: explanation */}
          <div className="flex-1 lg:px-11 pb-6 lg:pb-8 flex flex-col gap-4">
            {questionForComponents.explanationText ? (
              <div className="p-7 rounded-lg border border-border-input-field">
                <Span className="font-semibold text-lg">Correzione commentata</Span>
                <div className="text-text-body-primary">
                  <LaTeXRenderer text={questionForComponents.explanationText} />
                </div>
                {questionForComponents.explanationImages?.map((src, i) => (
                  <FullscreenImage key={i} src={src} className="max-w-full max-h-80 object-contain" />
                ))}
              </div>
            ) : (
              <Span className="text-text-body-description italic">Nessuna spiegazione disponibile per questa domanda.</Span>
            )}
          </div>
        </div>
      </div>
      <div className="pb-5">
        <QuestionNav
          questions={questionsForNav}
          questionIndex={questionIndex}
          previous={() => setQuestionIndex((i) => Math.max(0, i - 1))}
          next={() =>
            setQuestionIndex((i) => Math.min(questionsForNav.length - 1, i + 1))
          }
          goTo={(i) => setQuestionIndex(i)}
        />
      </div>
    </PageWhite>
  );
}
